/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MeasurementDataService } from '../../services/measurement-data.service';
import { Frame, ParameterRecord, MeasurementData, RESPONSE_COLORS } from '../../models/measurement-view.types';
import { format } from 'date-fns';

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    standalone: false
})
export class DataTableComponent implements OnInit, OnDestroy {
    @Input() public timeFormat: 'absolute' | 'relative' = 'absolute';

    private destroy$ = new Subject<void>();

    public selectedFrame: Frame | null = null;
    public currentMeasurement: MeasurementData | null = null;
    public hoveredWaveIndex: number | null = null;

    public constructor(private measurementDataService: MeasurementDataService) {}

    public ngOnInit(): void {
        this.measurementDataService.selectedFrame$.pipe(takeUntil(this.destroy$)).subscribe((frame) => {
            this.selectedFrame = frame;
        });

        this.measurementDataService.currentMeasurement$.pipe(takeUntil(this.destroy$)).subscribe((measurement) => {
            this.currentMeasurement = measurement;
        });

        this.measurementDataService.hoveredWaveIndex$.pipe(takeUntil(this.destroy$)).subscribe((index) => {
            this.hoveredWaveIndex = index;
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public formatTime(seconds: number): string {
        if (this.timeFormat === 'absolute' && this.currentMeasurement) {
            const startDate = new Date(`${this.currentMeasurement.meta.date}T${this.currentMeasurement.meta.time}`);
            return format(new Date(startDate.getTime() + seconds * 1000), 'yyyy-MM-dd HH:mm:ss');
        } else {
            return this.measurementDataService.formatSecondsToTime(seconds);
        }
    }

    public getTofrValue(): string {
        if (!this.selectedFrame) return '-';

        if (this.selectedFrame.frame_type === 'TOFR' || this.selectedFrame.frame_type === 'PTC-TOFR') {
            return this.selectedFrame.value.toString();
        }

        return '-';
    }

    public isNoise(): boolean {
        return this.selectedFrame?.wave_data.is_noise || false;
    }

    public getTypeDisplay(): string {
        if (!this.selectedFrame) return '-';

        if (this.selectedFrame.wave_data.is_noise) {
            return 'NOISE';
        }

        return this.selectedFrame.formatted_value || this.selectedFrame.frame_type;
    }

    public getFrameTypeLabel(): string {
        if (!this.selectedFrame) return '';
        return this.selectedFrame.wave_data.is_noise ? 'NOISE' : this.selectedFrame.frame_type;
    }

    public getFrameTypeBadgeStyle(): Record<string, string> {
        if (!this.selectedFrame) return {};
        // TOFR/TOFC chart color is near-white; use the same blue as the tof-ratio badge instead
        const frameType = this.selectedFrame.frame_type;
        const isTofrOrTofc = frameType === 'TOFR' || frameType === 'TOFC';
        const color = this.selectedFrame.wave_data.is_noise
            ? '#969696'
            : isTofrOrTofc
              ? '#000099'
              : (RESPONSE_COLORS[frameType] ?? '#ccc');
        return {
            'background-color': color,
            color: '#fff'
        };
    }

    public getAmplitudeDisplay(): string {
        if (!this.selectedFrame) return '-';

        if (this.selectedFrame.frame_type === 'CAL_UNPROCESSED') {
            return '-';
        }

        const amplitude = this.measurementDataService.roundValue(this.selectedFrame.wave_data.amplitude, 2);
        const responseTypeLabel = this.selectedFrame.frame_type === 'PTC' ? 'PTC' : 'T';

        return `${amplitude} (on ${responseTypeLabel}1)`;
    }

    public getHoveredAmplitudeDisplay(): string {
        if (this.hoveredWaveIndex === null || !this.selectedFrame) {
            return this.getAmplitudeDisplay();
        }

        const responseTypeLabel = this.selectedFrame.frame_type === 'PTC' ? 'PTC' : 'T';
        const wave = this.selectedFrame.wave_data.waves[this.hoveredWaveIndex];
        if (!wave || wave.noise || !wave.signal) {
            return `- (on ${responseTypeLabel}${this.hoveredWaveIndex + 1})`;
        }

        const amplitude = this.measurementDataService.roundValue(wave.peak_value - wave.valley_value, 2);
        return `${amplitude} (on ${responseTypeLabel}${this.hoveredWaveIndex + 1})`;
    }

    public getDateTimeDisplay(): string {
        if (!this.selectedFrame) return '-';

        return this.formatTime(this.selectedFrame.time);
    }

    public getModeDisplay(): string {
        if (!this.selectedFrame || !this.currentMeasurement) return '-';

        const validModes = ['TOF', 'PTC', 'PTC-ST', 'CAL', 'ST'];
        const parameterRecord = this.getParameterRecord();
        const mode = parameterRecord?.mode.toUpperCase();

        // Priority 1: Use the parameter record mode if it exists and is valid
        if (mode && validModes.includes(mode)) {
            return mode;
        }

        // // Priority 2: Try to get from selected frame's parameter
        // if (this.selectedFrame && this.currentMeasurement) {
        //     const frameParameter = this.currentMeasurement.data.find(
        //         (entry) =>
        //             entry.type === 'parameter' &&
        //             'parameter_index' in entry &&
        //             entry.parameter_index === this.selectedFrame.parameter_index &&
        //             'mode' in entry &&
        //             entry.mode
        //     );

        //     if (
        //         frameParameter &&
        //         'mode' in frameParameter &&
        //         frameParameter.mode &&
        //         validModes.includes(frameParameter.mode)
        //     ) {
        //         return frameParameter.mode;
        //     }
        // }

        // // Priority 3: Fallback to frame type analysis only if no valid mode found
        // const frameType = this.selectedFrame.frame_type;

        // if (frameType.includes('PTC')) {
        //     if (frameType === 'PTC-ST') {
        //         return 'PTC-ST';
        //     }
        //     return 'PTC';
        // }

        // if (frameType === 'ST') {
        //     return 'PTC-ST';
        // }

        // if (frameType === 'CAL' || frameType === 'CAL_UNPROCESSED') {
        //     return 'CAL';
        // }

        // If no valid mode found, return dash
        return '-';
    }

    public getStimulusCurrentDisplay(): string {
        if (!this.selectedFrame || !this.currentMeasurement) return '-';

        const parameterRecord = this.getParameterRecord();
        return parameterRecord ? `${parameterRecord.stimulus_current} mA` : '-';
    }

    public getPulseWidthDisplay(): string {
        if (!this.selectedFrame || !this.currentMeasurement) return '-';

        const parameterRecord = this.getParameterRecord();
        return parameterRecord ? `${parameterRecord.stimulus_pulse_width} μs` : '-';
    }

    public getIntervalDisplay(): string {
        if (!this.selectedFrame || !this.currentMeasurement) return '-';

        const parameterRecord = this.getParameterRecord();
        if (!parameterRecord) return '-';

        const intervalValue = this.getIntervalValue(parameterRecord);
        const intervalUnit = this.getIntervalUnit();

        return `${intervalValue} ${intervalUnit}`;
    }

    public getNoiseDisplay(): string {
        if (!this.selectedFrame) return '-';

        if (this.selectedFrame.frame_type === 'CAL_UNPROCESSED') {
            return '-';
        }

        return this.selectedFrame.wave_data.is_noise ? 'Yes' : 'No';
    }

    private getParameterRecord(): ParameterRecord | null {
        if (!this.selectedFrame || !this.currentMeasurement) return null;

        // Find the parameter record that appears just before this frame in the data
        let lastParameterRecord: ParameterRecord | null = null;
        let foundFrame = false;

        for (const entry of this.currentMeasurement.data) {
            if (entry.type === 'parameter') {
                if (!foundFrame) {
                    lastParameterRecord = entry;
                }
            } else if (entry.type === 'frame' && entry === this.selectedFrame) {
                foundFrame = true;
                break;
            }
        }

        return lastParameterRecord;
    }

    private getIntervalValue(parameterRecord: ParameterRecord): number {
        // Determine which interval to use based on mode
        let result = 0;
        switch (parameterRecord.mode.toUpperCase()) {
            case 'PTC':
                result = parameterRecord.ptc_interval;
                break;
            case 'ST':
                result = parameterRecord.st_interval;
                break;
            default:
                result = parameterRecord.tof_interval;
        }
        result = result / 10;
        return result;
    }

    private getIntervalUnit(): string {
        return 's';
    }
}
